/**
 * `/api/coffee` — every call the coffee module makes, through core's `apiFetch`.
 */
import type {
  CoffeeAccountResponse,
  CoffeeAccountsResponse,
  CoffeeMachineCountersResponse,
  CoffeeMachinesResponse,
  CoffeeMachineStatusResponse,
  CoffeeProvidersResponse,
  CoffeeStandbyResponse,
  CoffeeWakeResponse,
  CreateCoffeeAccountRequest,
  OkResponse,
  UpdateCoffeeAccountRequest,
} from '@forsys/shared';
import { apiFetch } from '../../api/client';

/** Needs `coffee.manage`. */
export function listProviders(): Promise<CoffeeProvidersResponse> {
  return apiFetch<CoffeeProvidersResponse>('/coffee/providers');
}

export function listAccounts(): Promise<CoffeeAccountsResponse> {
  return apiFetch<CoffeeAccountsResponse>('/coffee/accounts');
}

/** Signs in with the credentials before anything is stored. */
export function createAccount(request: CreateCoffeeAccountRequest): Promise<CoffeeAccountResponse> {
  return apiFetch<CoffeeAccountResponse>('/coffee/accounts', { method: 'POST', json: request });
}

export function updateAccount(id: number, request: UpdateCoffeeAccountRequest): Promise<CoffeeAccountResponse> {
  return apiFetch<CoffeeAccountResponse>(`/coffee/accounts/${id}`, { method: 'PATCH', json: request });
}

export function deleteAccount(id: number): Promise<OkResponse> {
  return apiFetch<OkResponse>(`/coffee/accounts/${id}`, { method: 'DELETE' });
}

export function syncAccount(id: number): Promise<CoffeeAccountResponse> {
  return apiFetch<CoffeeAccountResponse>(`/coffee/accounts/${id}/sync`, { method: 'POST' });
}

/** Needs `coffee.use`. */
export function listMachines(): Promise<CoffeeMachinesResponse> {
  return apiFetch<CoffeeMachinesResponse>('/coffee/machines');
}

/** `fresh`: Refresh — skip the server's cache and ask the machine for a new report. Takes a few seconds. */
export function getMachineStatus(id: number, fresh = false): Promise<CoffeeMachineStatusResponse> {
  return apiFetch<CoffeeMachineStatusResponse>(`/coffee/machines/${id}/status${fresh ? '?fresh=true' : ''}`);
}

/** Answers when the cloud accepted the wake — about twenty seconds — not when the machine is on. */
export function wakeMachine(id: number): Promise<CoffeeWakeResponse> {
  return apiFetch<CoffeeWakeResponse>(`/coffee/machines/${id}/wake`, { method: 'POST' });
}

/** Answers when the cloud accepted it. */
export function standbyMachine(id: number): Promise<CoffeeStandbyResponse> {
  return apiFetch<CoffeeStandbyResponse>(`/coffee/machines/${id}/standby`, { method: 'POST' });
}

/** As the machine last sent them. `fresh`: Refresh — ask the machine for new ones too. */
export function getMachineCounters(id: number, fresh = false): Promise<CoffeeMachineCountersResponse> {
  return apiFetch<CoffeeMachineCountersResponse>(`/coffee/machines/${id}/counters${fresh ? '?fresh=true' : ''}`);
}
